import { FC } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from 'recharts';
import dayjs from 'dayjs';
import { IHooperSeriesMetric } from '../../../../models/injury';
import { ChartEmptyState } from './_shared/ChartEmptyState';
import {
  chartAxisTick,
  chartGridProps,
  chartLegendStyle,
  chartTooltipContentStyle,
  chartTooltipItemStyle,
  chartTooltipLabelStyle,
} from '../../../../shared/charts/chartTheme';

interface Props {
  data: IHooperSeriesMetric | null;
  period?: string;
  height?: number;
}

const LINE_CONFIG = [
  { key: 'fatigue', stroke: 'var(--ri-critical)', label: 'Fatica' },
  { key: 'sleepQuality', stroke: '#5BC9E0', label: 'Qualita sonno' },
  { key: 'muscleSoreness', stroke: 'var(--ri-warning)', label: 'Dolori muscolari' },
  { key: 'stressLevel', stroke: 'var(--c-text-0)', label: 'Stress' },
] as const;

export const HooperSeriesChart: FC<Props> = ({ data, height }) => {
  if (!data || !data.series || data.series.length === 0) {
    return <ChartEmptyState />;
  }

  return (
    <ResponsiveContainer width="100%" height={height ?? 220}>
      <LineChart data={data.series} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid {...chartGridProps} />
        <XAxis dataKey="date" tickFormatter={(v) => dayjs(v).format('DD/MM')} tick={chartAxisTick} />
        <YAxis domain={[1, 5]} tick={chartAxisTick} />
        <Tooltip
          contentStyle={chartTooltipContentStyle}
          labelStyle={chartTooltipLabelStyle}
          itemStyle={chartTooltipItemStyle}
          formatter={(value, name) => {
            const config = LINE_CONFIG.find((c) => c.key === name);
            return [value, config?.label ?? name];
          }}
          labelFormatter={(label) => dayjs(label).format('DD/MM/YYYY')}
        />
        <Legend
          formatter={(value) => {
            const config = LINE_CONFIG.find((c) => c.key === value);
            return config?.label ?? value;
          }}
          wrapperStyle={chartLegendStyle}
        />
        {LINE_CONFIG.map((cfg) => (
          <Line key={cfg.key} type="monotone" dataKey={cfg.key} stroke={cfg.stroke} strokeWidth={2} dot={false} />
        ))}
        {data.injuryMarkers?.map((marker) => (
          <ReferenceLine key={marker.injuryId} x={marker.date} stroke="var(--ri-critical)" strokeWidth={1.5} />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};
